/**
 * batch-61 uitrusting-credits-maten (FO-445) — pure regels voor de Van Ysselt-
 * saldo-synchronisatie. Geen DB, geen IO: de API-service en de crons roepen dit
 * aan, de tests dekken de randen (NL-dag rond middernacht, kwartaalgrens,
 * eerste werkdag met feestdag op de 1e).
 *
 * Tijdzone: alle "dag"-afleidingen gebeuren in Europe/Amsterdam (CLAUDE.md);
 * de DB blijft UTC.
 */

import { isWerkdag } from './uren-calculations'

const TIME_ZONE = 'Europe/Amsterdam'

/** NL-kalenderdag (yyyy-MM-dd) van een instant: de bundelsleutel per medewerker per dag. */
export function nlDagSleutel(instant: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant)
}

/** Uur (0-23) van een instant in NL-tijd. */
export function nlUur(instant: Date): number {
  const uur = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, hour: '2-digit', hour12: false }).format(
      instant,
    ),
  )
  // Sommige runtimes geven '24' om middernacht.
  return uur % 24
}

export type VanYsseltBundelModusWaarde = 'DAG' | 'DIRECT'

/**
 * Mag een wachtende bericht-rij nu de deur uit?
 *  - DIRECT: altijd (de cron is dan alleen het vangnet voor gemiste post-commit-verzendingen).
 *  - DAG:    de rij hoort bij een EERDERE NL-dag, of bij vandaag zodra het
 *            dagbundel-uur (default 17:00 NL) is bereikt. Zo krijgt Van Ysselt
 *            één mail per medewerker per dag met alle leveringen van die dag.
 */
export function isDagbundelVerzendbaar(
  now: Date,
  bundelDatum: string,
  modus: VanYsseltBundelModusWaarde,
  dagbundelUur = 17,
): boolean {
  if (modus === 'DIRECT') return true
  const vandaag = nlDagSleutel(now)
  if (bundelDatum < vandaag) return true
  if (bundelDatum > vandaag) return false
  return nlUur(now) >= dagbundelUur
}

/** "Q3 2026" — de kwartaal-context in elk bericht (FO-445 §3B.5). */
export function kwartaalLabel(jaar: number, kwartaal: number): string {
  return `Q${kwartaal} ${jaar}`
}

/**
 * Ledger-snapshot zoals Van Ysselt 'm kent: het saldo zonder de interne
 * reserveringen (die tellen apart als "gereserveerd", §3B.5).
 */
export function vanYsseltSaldo(saldo: { toegekend: number; gebruikt: number }): number {
  return saldo.toegekend - saldo.gebruikt
}

/** Eén regel van een bestelling zoals de credit-verdeler 'm nodig heeft. */
export interface LeverwijzeCreditRegel {
  omschrijving: string
  maat: string | null
  aantal: number
  aantalVoorraad: number
  creditsPerStuk: number
  /** Regel telt niet mee in het creditbudget (PBM/VEILIGHEID). */
  creditVrij: boolean
  /** De regel routeert naar Van Ysselt (leverancier-cascade). */
  vanYsselt: boolean
}

export interface LeverwijzeCreditDeel {
  regels: Array<{
    omschrijving: string
    maat: string | null
    aantal: number
    creditsPerStuk: number
    credits: number
  }>
  credits: number
  stuks: number
}

/**
 * Splits de Van Ysselt-regels van een verwerkte bestelling in het voorraad-deel
 * (correctiebericht) en het extern-deel (saldo-blok in de bestelmail).
 * Credit-vrije regels tellen in geen van beide mee; niet-Van Ysselt-regels
 * evenmin (§5.1: andere leverancier = geen credits, geen bericht).
 */
export function verdeelCreditsPerLeverwijze(
  regels: ReadonlyArray<LeverwijzeCreditRegel>,
): { voorraad: LeverwijzeCreditDeel; extern: LeverwijzeCreditDeel } {
  const voorraad: LeverwijzeCreditDeel = { regels: [], credits: 0, stuks: 0 }
  const extern: LeverwijzeCreditDeel = { regels: [], credits: 0, stuks: 0 }
  for (const r of regels) {
    if (!r.vanYsselt || r.creditVrij) continue
    const uitVoorraad = Math.max(0, Math.min(r.aantalVoorraad, r.aantal))
    const uitExtern = Math.max(0, r.aantal - uitVoorraad)
    if (uitVoorraad > 0) {
      const credits = uitVoorraad * r.creditsPerStuk
      voorraad.regels.push({
        omschrijving: r.omschrijving,
        maat: r.maat,
        aantal: uitVoorraad,
        creditsPerStuk: r.creditsPerStuk,
        credits,
      })
      voorraad.credits += credits
      voorraad.stuks += uitVoorraad
    }
    if (uitExtern > 0) {
      const credits = uitExtern * r.creditsPerStuk
      extern.regels.push({
        omschrijving: r.omschrijving,
        maat: r.maat,
        aantal: uitExtern,
        creditsPerStuk: r.creditsPerStuk,
        credits,
      })
      extern.credits += credits
      extern.stuks += uitExtern
    }
  }
  return { voorraad, extern }
}

/** Korte, stabiele hash over een set ids (volgorde-onafhankelijk); puur dedup-signaal. */
function hashIds(ids: ReadonlyArray<string>): string {
  const str = [...ids].sort().join('|')
  let h = 0
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) | 0
  return (h >>> 0).toString(36)
}

/**
 * Idempotency-key van een dagbundel-mail: medewerker + NL-dag + INHOUD (de
 * berichtdelen) + poging. Dezelfde set delen in dezelfde poging dedupet
 * (dubbele cron-tick); een herhaling na een bezorgfout krijgt een nieuwe
 * poging en dus een nieuwe mail (§5.5). De inhoud zit in de key omdat één
 * medewerker op één dag meer dan één bundel kan krijgen (DIRECT-modus, of
 * "nu versturen" nadat de dagbundel al weg is): zonder inhoud-hash zou de
 * MailService de tweede bundel op de eerste mail dedupen en kreeg Van Ysselt
 * de nieuwe delen nooit.
 */
export function dagbundelIdempotencyKey(
  employeeId: string,
  bundelDatum: string,
  poging: number,
  berichtIds: ReadonlyArray<string> = [],
): string {
  const inhoud = berichtIds.length > 0 ? `-${hashIds(berichtIds)}` : ''
  return `vanysselt-saldo-${employeeId}-${bundelDatum}${inhoud}-p${poging}`
}

/** Idempotency-key van het maandoverzicht: (jaar, maand, poging). */
export function maandoverzichtIdempotencyKey(jaar: number, maand: number, poging: number): string {
  return `vanysselt-maandoverzicht-${jaar}-${String(maand).padStart(2, '0')}-p${poging}`
}

/** Vorige kalendermaand (1-12) van (jaar, maand). */
export function vorigeMaand(jaar: number, maand: number): { jaar: number; maand: number } {
  if (maand === 1) return { jaar: jaar - 1, maand: 12 }
  return { jaar, maand: maand - 1 }
}

/** Jaar/maand/dag van een instant in NL-tijd. */
export function nlJaarMaandDag(instant: Date): { jaar: number; maand: number; dag: number } {
  const [jaar, maand, dag] = nlDagSleutel(instant).split('-').map(Number)
  return { jaar: jaar ?? 0, maand: maand ?? 0, dag: dag ?? 0 }
}

/**
 * Is `now` (NL-tijd) de EERSTE werkdag van zijn maand? Weekend en verplichte
 * feestdagen tellen niet als werkdag (isWerkdag), dus 1 januari of een
 * maandag-1e-paasdag schuift de run door naar de eerstvolgende werkdag.
 * Datums worden op kalenderdag vergeleken (UTC-middag als representant zodat
 * de DST-overgang de dag niet verschuift).
 */
export function isEersteWerkdagVanMaand(now: Date, feestdagen: ReadonlyArray<Date>): boolean {
  const { jaar, maand, dag } = nlJaarMaandDag(now)
  const vandaag = new Date(Date.UTC(jaar, maand - 1, dag, 12))
  if (!isWerkdag(vandaag, [...feestdagen])) return false
  for (let d = 1; d < dag; d += 1) {
    const eerder = new Date(Date.UTC(jaar, maand - 1, d, 12))
    if (isWerkdag(eerder, [...feestdagen])) return false
  }
  return true
}

/** Maandlabel in het Nederlands ("augustus 2026"). */
export function vanYsseltMaandLabel(jaar: number, maand: number): string {
  return new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(jaar, maand - 1, 15, 12)),
  )
}

/**
 * Bepaal de deling-status van een set berichtdelen die bij één mutatie/
 * bestelling horen (portal: "gedeeld met Van Ysselt op {datum}").
 */
export function bepaalVanYsseltDeling(
  delen: ReadonlyArray<{ status: 'WACHTEND' | 'VERZONDEN' | 'MISLUKT' | 'OVERGESLAGEN'; verstuurdOp: Date | null }>,
): { gedeeld: boolean; gedeeldOp: Date | null; bezorgfout: boolean; wachtend: number } | null {
  if (delen.length === 0) return null
  const relevant = delen.filter((d) => d.status !== 'OVERGESLAGEN')
  if (relevant.length === 0) return null
  const bezorgfout = relevant.some((d) => d.status === 'MISLUKT')
  const wachtend = relevant.filter((d) => d.status === 'WACHTEND').length
  const gedeeld = relevant.every((d) => d.status === 'VERZONDEN')
  let gedeeldOp: Date | null = null
  if (gedeeld) {
    for (const d of relevant) {
      if (d.verstuurdOp && (gedeeldOp === null || d.verstuurdOp.getTime() > gedeeldOp.getTime())) {
        gedeeldOp = d.verstuurdOp
      }
    }
  }
  return { gedeeld, gedeeldOp, bezorgfout, wachtend }
}
